import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
// import RouteBar from './RouteBar'; // Uncomment if needed in PipelineLayout
ModuleRegistry.registerModules([AllCommunityModule]);

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const STRIPE_SIGNIN_URL = 'https://www.stripe.com';

function UserDashboard() {
  const { 
    user, 
    membership, 
    loading: authLoading, 
    csrfToken, 
    fetchCsrfToken, 
    hasActiveMembership, 
    fetchWrapper, 
    ready, 
    isInitializing // UPDATED: For full settle guard
  } = useAuth(); // UPDATED: Add ready, isInitializing, rpcWithRetry
  const [clientInfo, setClientInfo] = useState(null);
  const [profile, setProfile] = useState(null);
  // REMOVED: fullMembership (use context membership directly)
  const [sales, setSales] = useState([]);
  const [saleName, setSaleName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [saleSalesTax, setSaleSalesTax] = useState('0');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [tierDetails, setTierDetails] = useState(null); // NEW: For tier details (max_sales, etc.)
  const [showUpdatedMessage, setShowUpdatedMessage] = useState(false);
  const [updatedType, setUpdatedType] = useState('');
  // ADDED: Local fresh membership for display/gates (fetched via get_membership2)
  const [freshMembership, setFreshMembership] = useState(null);
  const [saleZip, setSaleZip] = useState('');
  const [zipSuggestions, setZipSuggestions] = useState([]);
  const [showSuppliesModal, setShowSuppliesModal] = useState(false);
  const [saleCreatedMessage, setSaleCreatedMessage] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [showDateErrorModal, setShowDateErrorModal] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState('');
  const [stripeConnectMessage, setStripeConnectMessage] = useState('');
  const [stripeConnectMessageType, setStripeConnectMessageType] = useState('info');
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);
  const [stripeDashboardUrl, setStripeDashboardUrl] = useState('');
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);
  const userId = user?.id || null;
  const skipNextSaleNameValidationRef = useRef(false);
  const bootRanRef = useRef(false);

  const renderStripeConnectMessage = useCallback((message) => {
    const text = String(message || '');
    const linkText = 'www.stripe.com';
    const linkIndex = text.indexOf(linkText);
    if (linkIndex === -1) return text;

    return (
      <>
        {text.slice(0, linkIndex)}
        <a
          href={STRIPE_SIGNIN_URL}
          target="_blank"
          rel="noreferrer"
          className="font-semibold underline"
        >
          {linkText}
        </a>
        {text.slice(linkIndex + linkText.length)}
      </>
    );
  }, []);

  console.log('UserDashboard: rendered) ');

  const rpcJson = useCallback(async (url, options) => {
    const res = await fetchWrapper(url, options);
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
    }
    return res.json();
  }, [fetchWrapper]);

  const rpcJsonCsrf = useCallback(async (endpoint, body, csrf) => {
    return rpcJson(`${process.env.REACT_APP_API_URL}/supabase/rpc/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
      body: JSON.stringify(body),
      credentials: 'include',
    });
  }, [rpcJson]);

  const postStripeConnect = useCallback(async (endpoint, body, csrf) => {
    return rpcJson(`${process.env.REACT_APP_API_URL}/onboarding/stripe-connect/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
      body: JSON.stringify(body || {}),
      credentials: 'include',
    });
  }, [rpcJson]);

  // NEW: Reusable retry wrapper for fetchWrapper calls (handles prop lag post-sign-in)
  const withRetry = useCallback(async (fetchFn, maxRetries = 2, baseDelay = 300) => {
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fetchFn();
      } catch (error) {
        lastError = error;
        const message = String(error?.message || '');
        const isRetryable = /HTTP 5\d\d/i.test(message)
          || /Failed to fetch|NetworkError/i.test(message);
        console.error(`[Dashboard] Fetch failed on attempt ${attempt + 1}:`, message);
        if (attempt < maxRetries && isRetryable) {
          await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt))); // Backoff
        } else {
          throw lastError;
        }
      }
    }
  }, []);

  // UPDATED: fetchFreshMembership - Use fetchWrapper + withRetry (keep proxy endpoint)
  const fetchFreshMembership = useCallback(async (userId) => {
    try {
      const fetchFn = () => rpcJson(`${process.env.REACT_APP_API_URL}/supabase/rpc/get_membership2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ p_user_id: userId }),
      });
      const data = await withRetry(fetchFn, 2, 300);
      const membershipData = Array.isArray(data) ? data[0] || null : data || null;
      setFreshMembership(membershipData);
    } catch (err) {
      console.error('get_membership2 error:', err);
      setError(`Membership refresh failed: ${err.message} (using cached)`);
      setFreshMembership(membership); // Fallback to context
    }
  }, [membership, rpcJson, withRetry]);

  // Similar for others (pattern: fetchWrapper + withRetry + fallback)
  const fetchClientInfo = useCallback(async (clientId) => {
    try {
      const fetchFn = () => rpcJson(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ p_client_id: clientId }),
      });
      const data = await withRetry(fetchFn, 2, 300);
      setClientInfo(Array.isArray(data) ? data[0] || null : data || null);
    } catch (err) {
      console.error('Select client error:', err);
      setError(`Client info fetch failed: ${err.message} (using cached)`);
    }
  }, [rpcJson, withRetry]);

  const fetchProfile = useCallback(async (clientId) => {
    try {
      const fetchFn = () => rpcJson(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ p_client_id: clientId }),
      });
      const data = await withRetry(fetchFn, 2, 300);
      setProfile(Array.isArray(data) ? data[0] || null : data || null);
    } catch (err) {
      console.error('Select profile error:', err);
      setError(`Profile fetch failed: ${err.message} (using cached)`);
    }
  }, [rpcJson, withRetry]);

  const fetchSales = useCallback(async (clientId) => {
    try {
      const fetchFn = () => rpcJson(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_all_sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ p_client_id: clientId }),
      });
      const data = await withRetry(fetchFn, 2, 300);
      const filteredSales = (Array.isArray(data) ? data : []).filter(sale => sale.status !== 'deleted');
      const processedSales = filteredSales.map(sale => ({
        ...sale,
        cc_collection_enabled: Boolean(sale.cc_collection_enabled),
        start_date: sale.start_date ? sale.start_date.split('T')[0] : null,
        end_date: sale.end_date ? sale.end_date.split('T')[0] : null,
      }));
      setSales(processedSales);
    } catch (err) {
      console.error('Select all sales error:', err);
      setError(err.message);
    }
  }, [rpcJson, withRetry]);

  const fetchTierDetails = useCallback(async (tierName) => {
    if (!tierName) return;
    try {
      const fetchFn = () => rpcJson(`${process.env.REACT_APP_API_URL}/supabase/rpc/get_tiers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await withRetry(fetchFn, 2, 300);
      const tiersList = Array.isArray(data) ? data : [];
      const normalizeTier = (value) =>
        value.toLowerCase().replace(/\s+membership$/, '').trim();
      const normalizedTier = normalizeTier(tierName);
      const tierData = tiersList.find((t) => {
        if (typeof t?.tier !== 'string') return false;
        return normalizeTier(t.tier) === normalizedTier;
      });
      setTierDetails(tierData || null);
    } catch (err) {
      console.error('Tier details error:', err);
      setTierDetails({
        tier: tierName,
        description: freshMembership?.description || membership?.description || '',
        max_sales: freshMembership?.max_sales ?? membership?.max_sales ?? 0,
        max_images_uploads: freshMembership?.max_images_uploads ?? membership?.max_images_uploads ?? 0
      });
    }
  }, [rpcJson, withRetry, freshMembership, membership]);

  const refreshStripeConnectStatus = useCallback(async ({
    showResultMessage = false,
    csrfOverride = null,
  } = {}) => {
    if (!userId) return null;
    const activeCsrf = csrfOverride || csrfToken || await fetchCsrfToken();
    if (!activeCsrf) {
      throw new Error('Security token unavailable. Refresh and try again.');
    }

    setStripeConnectLoading(true);
    try {
      const data = await postStripeConnect('status', {}, activeCsrf);
      if (data?.dashboard_url) {
        setStripeDashboardUrl(data.dashboard_url);
      }
      if (showResultMessage) {
        setStripeConnectMessage(data?.message || (
          data?.enabled
            ? 'Stripe Connect is enabled for credit card processing.'
            : 'Stripe Connect setup is not complete yet. Go to www.stripe.com, sign in, open the merchant dashboard, and verify that all registration and payout steps are finished.'
        ));
        setStripeConnectMessageType(data?.enabled ? 'success' : 'warning');
      }
      await fetchProfile(userId);
      return data;
    } finally {
      setStripeConnectLoading(false);
    }
  }, [csrfToken, fetchCsrfToken, fetchProfile, postStripeConnect, userId]);

  const handleStripeConnectStart = useCallback(async () => {
    if (!userId) return;
    setError('');
    setStripeConnectMessage('');
    setStripeConnectLoading(true);
    try {
      const activeCsrf = csrfToken || await fetchCsrfToken();
      if (!activeCsrf) {
        throw new Error('Security token unavailable. Refresh and try again.');
      }
      const data = await postStripeConnect('start', {}, activeCsrf);
      if (data?.dashboard_url) {
        setStripeDashboardUrl(data.dashboard_url);
      }
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      if (data?.enabled) {
        setStripeConnectMessage(data?.message || 'Stripe Connect is enabled for credit card processing.');
        setStripeConnectMessageType('success');
        await fetchProfile(userId);
        return;
      }
      throw new Error(data?.error || 'Stripe onboarding did not return a redirect URL.');
    } catch (err) {
      setStripeConnectMessage(err.message || 'Failed to start Stripe onboarding.');
      setStripeConnectMessageType('error');
    } finally {
      setStripeConnectLoading(false);
    }
  }, [csrfToken, fetchCsrfToken, fetchProfile, postStripeConnect, userId]);

  const handleStripeDashboardOpen = useCallback(async () => {
    if (stripeDashboardUrl) {
      window.location.assign(stripeDashboardUrl);
      return;
    }
    try {
      const data = await refreshStripeConnectStatus({ showResultMessage: false });
      const nextUrl = data?.dashboard_url || '';
      if (nextUrl) {
        setStripeDashboardUrl(nextUrl);
        window.location.assign(nextUrl);
        return;
      }
      setStripeConnectMessage('Stripe merchant dashboard is not available yet. Complete Stripe setup first.');
      setStripeConnectMessageType('warning');
    } catch (err) {
      setStripeConnectMessage(err.message || 'Failed to open Stripe merchant dashboard.');
      setStripeConnectMessageType('error');
    }
  }, [refreshStripeConnectStatus, stripeDashboardUrl]);

  const fetchZipSuggestions = async (query) => {
    if (!query || query.length < 3) {
      setZipSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&limit=5&filter=countrycode:us&apiKey=${process.env.REACT_APP_GEOAPIFY_KEY}`
      );
      const data = await response.json();
      const suggestions = data.features
        .map(f => f.properties)
        .filter(p => p.postcode && /^\d{5}$/.test(p.postcode));
      setZipSuggestions(suggestions);
    } catch (err) {
      console.error('Geoapify error:', err);
      setZipSuggestions([]);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const updated = urlParams.get('updated');
    if (updated === 'true') {
      setUpdatedType(urlParams.get('updated_type') || 'free');
      setShowUpdatedMessage(true);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeConnect = urlParams.get('stripe_connect');
    if (!stripeConnect || !userId || !csrfToken) return;

    const verifyReturn = async () => {
      if (stripeConnect === 'return') {
        try {
          let latestStatus = null;
          const retryDelays = [0, 1500, 3000, 5000];
          for (const delayMs of retryDelays) {
            if (delayMs > 0) await wait(delayMs);
            latestStatus = await refreshStripeConnectStatus({
              showResultMessage: false,
              csrfOverride: csrfToken,
            });
            if (latestStatus?.enabled) break;
          }

          setStripeConnectMessage(latestStatus?.message || (
            latestStatus?.enabled
              ? 'Stripe Connect is enabled for credit card processing.'
              : 'Stripe Connect setup is not complete yet. Go to www.stripe.com, sign in, open the merchant dashboard, and verify that all registration and payout steps are finished.'
          ));
          setStripeConnectMessageType(latestStatus?.enabled ? 'success' : 'warning');
        } catch (err) {
          setStripeConnectMessage(err.message || 'Failed to verify Stripe onboarding status.');
          setStripeConnectMessageType('error');
        }
      } else if (stripeConnect === 'error') {
        setStripeConnectMessage('Stripe onboarding could not be completed. Please try again.');
        setStripeConnectMessageType('error');
      } else if (stripeConnect === 'not_started') {
        setStripeConnectMessage('Stripe Connect onboarding has not been started.');
        setStripeConnectMessageType('warning');
      }

      urlParams.delete('stripe_connect');
      const nextSearch = urlParams.toString();
      navigate(
        {
          pathname: window.location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true }
      );
    };

    verifyReturn();
  }, [csrfToken, navigate, refreshStripeConnectStatus, userId]);

  // UPDATED: Guard useEffect - Wait for ready && !isInitializing (full session settle); route to plain /member-maint2
  useEffect(() => {
    if (authLoading || !ready || isInitializing || !userId) {
      console.log('UserDashboard: Waiting for auth ready / user...');
      return;
    }

    // Ensure CSRF is available before running one-time boot reads.
    // This prevents a transient CSRF wobble from causing permanent missing data.
    if (!csrfToken) {
      console.log('UserDashboard: Waiting for CSRF token before boot...');
      fetchCsrfToken().catch((e) => console.warn('UserDashboard: fetchCsrfToken failed:', e));
      return;
    }

    setLoading(false);

    if (!hasActiveMembership) {
      console.log('No active membership, redirecting to member-maint2');
      navigate('/estate-sales-pipeline/member-maint2');
      return;
    }

    // Run boot reads only once per page load once CSRF is ready.
    if (bootRanRef.current) return;
    bootRanRef.current = true;

    const boot = async () => {
      fetchFreshMembership(userId);
      fetchClientInfo(userId);
      fetchProfile(userId);
      fetchSales(userId);
    };

    boot();
  }, [
    authLoading,
    ready,
    isInitializing,
    hasActiveMembership,
    userId,
    csrfToken,
    fetchCsrfToken,
    fetchFreshMembership,
    fetchClientInfo,
    fetchProfile,
    fetchSales,
    navigate
  ]);

  const tierName = (freshMembership?.tier || membership?.tier || '').trim();
  const isFreeMembership = useMemo(() => /free/i.test(tierName), [tierName]);
  const profileCcCollectionEnabled = Boolean(profile?.cc_collection_enabled);
  const showStripeConnectCta = Boolean(profile) && !isFreeMembership && !profileCcCollectionEnabled;

  useEffect(() => {
    if (!tierName) return;
    fetchTierDetails(tierName);
  }, [tierName, fetchTierDetails]);
  
  const isValidUSZip = (zip) => {
  // Must be exactly 5 digits
  if (!/^\d{5}$/.test(zip)) return false;

  const n = parseInt(zip, 10);

  // Block all digits the same: 11111, 22222, 33333, etc.
  if (/^(\d)\1{4}$/.test(zip)) return false;

  // Explicitly block common test/demo ZIPs
  if (zip === '12345' || zip === '54321') return false;

  // Real US ZIP range: 00601 to 99950 (excludes 00000–00600 and 99951–99999)
  return n >= 601 && n <= 99950;
};

  const parsePercentValue = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return 0;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    if (n < 0 || n > 100) return null;
    return Number(n.toFixed(2));
  };

  const parseWholePercentValue = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return 0;
    if (!/^\d+$/.test(raw)) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    if (n < 0 || n > 100) return null;
    return Math.trunc(n);
  };

  const formatDateInput = (value) => {
    if (value.includes('/')) {
      const rawParts = value.split('/');
      const month = (rawParts[0] || '').replace(/\D/g, '').slice(0, 2);
      const day = (rawParts[1] || '').replace(/\D/g, '').slice(0, 2);
      const year = (rawParts[2] || '').replace(/\D/g, '').slice(0, 4);
      const parts = [month, day, year].filter((part) => part !== '');
      const trailingSlash = value.endsWith('/');
      return `${parts.join('/')}${trailingSlash ? '/' : ''}`;
    }
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const MIN_SALE_YEAR = 2026;
  const getEffectiveYear = () => Math.max(new Date().getFullYear(), MIN_SALE_YEAR);

  const normalizeDateInput = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split('-');
      return `${month}/${day}/${year}`;
    }
    const parts = trimmed.split('/').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const month = parts[0].padStart(2, '0');
      const day = (parts[1] || '').padStart(2, '0');
      let year = parts[2] || '';
      const monthNum = Number(month);
      const dayNum = Number(day);
      const hasValidMonthDay = Number.isInteger(monthNum)
        && Number.isInteger(dayNum)
        && monthNum >= 1
        && monthNum <= 12
        && dayNum >= 1
        && dayNum <= 31;
      if (!year && parts.length === 2 && hasValidMonthDay) {
        year = String(getEffectiveYear());
        return `${month}/${day}/${year}`;
      }
      if (year.length === 2) year = `20${year}`;
      if (year.length === 4) {
        return `${month}/${day}/${year}`;
      }
    }
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 8) {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }
    if (digits.length === 6) {
      const year = `20${digits.slice(4)}`;
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${year}`;
    }
    return trimmed;
  };

  const isValidDateInput = (value) => {
    const normalized = normalizeDateInput(value);
    const match = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return false;
    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return false;
    if (year < MIN_SALE_YEAR) return false;
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const toISODate = (value) => {
    if (!isValidDateInput(value)) return '';
    const normalized = normalizeDateInput(value);
    const [month, day, year] = normalized.split('/');
    return `${year}-${month}-${day}`;
  };

  const showDateError = (message) => {
    setDateErrorMessage(message);
    setShowDateErrorModal(true);
  };

  const isEndBeforeStart = (startISO, endISO) => {
    if (!startISO || !endISO) return false;
    return endISO < startISO;
  };

  const openDatePicker = (ref) => {
    const input = ref.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  };
  const handleCreateSale = async () => {
    if (!userId) return;
    if (maxSalesReached) {
      setError('You have reached your maximum sales limit. Delete a sale to create a new one.');
      return;
    }
    const normalizedStartDate = toISODate(startDate);
    const normalizedEndDate = endDate ? toISODate(endDate) : '';
    const normalizedDiscPct = 0;
    const normalizedSalesTax = parsePercentValue(saleSalesTax);
    if (!saleName.trim() || !normalizedStartDate || !isValidUSZip(saleZip)) {
      setError('Please enter a valid start date and 5-digit US ZIP code (e.g., 90210).');
      return;
    }
    if (normalizedSalesTax === null) {
      setError('Sales Tax % must be 0-100.');
      return;
    }
    if (endDate && !normalizedEndDate) {
      setError('Please enter a valid end date.');
      return;
    }
    if (normalizedEndDate && isEndBeforeStart(normalizedStartDate, normalizedEndDate)) {
      showDateError('End date must be the same day or after the start date.');
      return;
    }

    setLoading(true);
    setError('');
    let currentCsrf = csrfToken;

    try {
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();

      // Check for duplicate sale name
    const existingSales = await rpcJsonCsrf('select_all_sales', { p_client_id: userId }, currentCsrf);
    const existingSalesList = Array.isArray(existingSales) ? existingSales : [];
    if (existingSalesList.some(sale => String(sale?.sale_name || '').toLowerCase() === saleName.trim().toLowerCase())) {
      setError('Sale name already exists. Choose a different name.');
      setLoading(false);
      return;
    }

    // CREATE SALE WITH ZIP CODE
    const insertResult = await rpcJsonCsrf('insert_sale', {
      p_client_id: userId,
      p_sale_name: saleName.trim(),
      p_start_date: normalizedStartDate,
      p_end_date: normalizedEndDate || null,
      p_sale_zip: saleZip.trim(),
      p_cc_collection_enabled: profileCcCollectionEnabled
    }, currentCsrf);

      let createdSaleId =
        (Array.isArray(insertResult) ? insertResult[0]?.id : insertResult?.id) ||
        (typeof insertResult === 'string' ? insertResult : null);

      if (!createdSaleId) {
        const refreshedSales = await rpcJsonCsrf('select_all_sales', { p_client_id: userId }, currentCsrf);
        const candidates = (Array.isArray(refreshedSales) ? refreshedSales : [])
          .filter((sale) =>
            String(sale?.sale_name || '').trim().toLowerCase() === saleName.trim().toLowerCase()
          )
          .sort((a, b) => String(b?.created_at || '').localeCompare(String(a?.created_at || '')));
        createdSaleId = candidates[0]?.id || null;
      }

      if (createdSaleId) {
        await rpcJsonCsrf('update_sale_disc_pct', {
          p_client_id: userId,
          p_sale_id: createdSaleId,
          p_disc_pct: normalizedDiscPct,
        }, currentCsrf);
        await rpcJsonCsrf('update_sale_sales_tax', {
          p_client_id: userId,
          p_sale_id: createdSaleId,
          p_sales_tax: normalizedSalesTax,
        }, currentCsrf);
      }

      // SUCCESS — refresh and clear form
      await fetchSales(userId);
      setSaleName('');
      setStartDate('');
      setEndDate('');
      setSaleSalesTax('0');
      setSaleZip('');
      setZipSuggestions([]);
      setError('');
      setSaleCreatedMessage(true);
      setTimeout(() => setSaleCreatedMessage(false), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
   
  const handleDeleteSale = (sale) => {
    const hasCounts = sale.image_count > 0 || sale.item_count > 0;
    const confirmMessage = hasCounts
      ? `This sale has ${sale.image_count} photos and ${sale.item_count} items. Delete anyway?`
      : 'Delete this sale?';
    setDeleteMessage(confirmMessage);
    setSaleToDelete(sale);
    setShowDeleteModal(true);
  };

  const confirmDeleteSale = async () => {
    if (!userId) return;
    if (!saleToDelete) {
      setShowDeleteModal(false);
      return;
    }
    let currentCsrf = csrfToken;
    try {
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      await rpcJsonCsrf('update_sale_status', {
        p_client_id: userId,
        p_sale_id: saleToDelete.id,
        p_status: 'deleted'
      }, currentCsrf);
      await fetchSales(userId);
    } catch (err) {
      console.error('Delete RPC error:', err);
      setError(`Delete failed: ${err.message}. Check console for details.`);
    } finally {
      setShowDeleteModal(false);
      setSaleToDelete(null);
      setDeleteMessage('');
    }
  };

  const onCellValueChanged = useCallback(async (event) => {
    if (!userId) return;
    const { colDef, data, newValue, oldValue } = event;
    if (newValue === oldValue) {
      return;
    }
    if (!userId) {
      console.error('No user ID, aborting update');
      alert('Not authenticated. Please refresh and try again.');
      return;
    }
    let currentCsrf = csrfToken;
    if (!currentCsrf) {
      try {
        currentCsrf = await fetchCsrfToken();
      } catch (csrfErr) {
        console.error('CSRF fetch failed:', csrfErr);
        alert('CSRF token fetch failed. Please refresh the page.');
        return;
      }
    }
    try {
    let body = { p_client_id: userId, p_sale_id: data.id };
    let rpcEndpoint = '';
    if (colDef.field === 'sale_name') {
      if (skipNextSaleNameValidationRef.current) {
        skipNextSaleNameValidationRef.current = false;
        return;
      }
      const normalizedName = newValue?.trim() || '';
      if (!normalizedName) {
        alert('Sale name cannot be empty.');
        skipNextSaleNameValidationRef.current = true;
        event.node.setDataValue(colDef.field, oldValue);
        event.api.startEditingCell({ rowIndex: event.node.rowIndex, colKey: colDef.field });
        return;
      }
      const duplicateName = sales.some((sale) =>
        sale.id !== data.id
        && String(sale.sale_name || '').trim().toLowerCase() === normalizedName.toLowerCase()
      );
      if (duplicateName) {
        alert('Sale name already exists. Choose a different name.');
        skipNextSaleNameValidationRef.current = true;
        event.node.setDataValue(colDef.field, oldValue);
        event.api.startEditingCell({ rowIndex: event.node.rowIndex, colKey: colDef.field });
        return;
      }
        body.p_sale_name = normalizedName;
        rpcEndpoint = 'update_sale_name';
      } else if (colDef.field === 'start_date') {
        const normalized = newValue ? normalizeDateInput(String(newValue)) : '';
      if (normalized && !isValidDateInput(normalized)) {
        alert('Invalid date format. Use MM/DD/YYYY.');
        event.node.setDataValue(colDef.field, oldValue);
        return;
      }
      const newStartISO = normalized ? toISODate(normalized) : null;
      const currentEndISO = data.end_date ? toISODate(String(data.end_date)) : null;
      if (newStartISO && currentEndISO && isEndBeforeStart(newStartISO, currentEndISO)) {
        showDateError('End date must be the same day or after the start date.');
        event.node.setDataValue(colDef.field, oldValue);
        return;
      }
      body.p_start_date = newStartISO;
      rpcEndpoint = 'update_sale_start_date';
    } else if (colDef.field === 'end_date') {
      const normalized = newValue ? normalizeDateInput(String(newValue)) : '';
      if (normalized && !isValidDateInput(normalized)) {
          alert('Invalid date format. Use MM/DD/YYYY.');
          event.node.setDataValue(colDef.field, oldValue);
          return;
        }
      const newEndISO = normalized ? toISODate(normalized) : null;
      const currentStartISO = data.start_date ? toISODate(String(data.start_date)) : null;
      if (newEndISO && currentStartISO && isEndBeforeStart(currentStartISO, newEndISO)) {
        showDateError('End date must be the same day or after the start date.');
        event.node.setDataValue(colDef.field, oldValue);
        return;
      }
      body.p_end_date = newEndISO;
      rpcEndpoint = 'update_sale_end_date';
    } else if (colDef.field === 'disc_pct') {
      const parsed = parseWholePercentValue(newValue);
      if (parsed === null) {
        alert('Discount % must be a whole number between 0 and 100.');
        event.node.setDataValue(colDef.field, oldValue);
        return;
      }
      body.p_disc_pct = parsed;
      rpcEndpoint = 'update_sale_disc_pct';
    } else if (colDef.field === 'sales_tax') {
      const parsed = parsePercentValue(newValue);
      if (parsed === null) {
        alert('Sales Tax % must be between 0 and 100.');
        event.node.setDataValue(colDef.field, oldValue);
        return;
      }
      body.p_sales_tax = parsed;
      rpcEndpoint = 'update_sale_sales_tax';
    } else {
      console.log('Ignoring non-editable field');
      return;
      }
      await rpcJsonCsrf(rpcEndpoint, body, currentCsrf);
    } catch (err) {
      console.error('Cell update RPC error:', err);
      alert(`Update failed: ${err.message}. Check console for details.`);
      event.node.setDataValue(colDef.field, oldValue);
    }
  }, [
    userId,
    csrfToken,
    fetchCsrfToken,
    isValidDateInput,
    normalizeDateInput,
    toISODate,
    rpcJsonCsrf,
    sales
  ]);

  useEffect(() => {
    if (!showDeleteModal) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowDeleteModal(false);
        setSaleToDelete(null);
        setDeleteMessage('');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showDeleteModal]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // Handle full TIMESTAMPTZ/ISO (e.g., '2025-10-22T17:32:16.535+00:00') – extract YYYY-MM-DD
    const datePart = dateStr.split('T')[0]; // Gets '2025-10-22'
    const [year, month, day] = datePart.split('-');
    return `${month}/${day}/${year}`;
  };

  const formatCurrency = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '$0.00';
    return n.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const columnDefs = useMemo(() => [
    {
      field: 'sale_name',
      headerName: 'Sale Name',
      flex: 2,
      minWidth: 120,
      editable: true,
      cellStyle: { cursor: 'text' }
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      flex: 0.85,
      minWidth: 112,
      sort: 'desc',
      valueFormatter: ({ value }) => formatDate(value),
      editable: true,
      cellStyle: { cursor: 'text' }
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      flex: 0.85,
      minWidth: 112,
      valueFormatter: ({ value }) => formatDate(value),
      editable: true,
      cellStyle: { cursor: 'text' }
    },
    {
      field: 'disc_pct',
      headerName: 'Disc %',
      flex: 0.5,
      minWidth: 85,
      editable: true,
      valueFormatter: ({ value }) => {
        const n = Number(value);
        return Number.isFinite(n) ? String(Math.round(n)) : '0';
      },
      cellStyle: { cursor: 'text' }
    },
    {
      field: 'sales_tax',
      headerName: 'Sales Tax %',
      flex: 0.65,
      minWidth: 110,
      editable: true,
      valueFormatter: ({ value }) => {
        const n = Number(value);
        return Number.isFinite(n) ? n.toFixed(2) : '0.00';
      },
      cellStyle: { cursor: 'text' }
    },
    {
      field: 'cc_collection_enabled',
      headerName: 'Stripe CC',
      flex: 0.55,
      minWidth: 96,
      editable: false,
      valueFormatter: ({ value }) => (value ? 'Enabled' : 'Manual'),
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'image_count',
      headerName: 'Photos',
      flex: 0.45,
      minWidth: 74,
      valueFormatter: ({ value }) => Number(value) || 0,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'item_count',
      headerName: 'Items',
      flex: 0.45,
      minWidth: 74,
      valueFormatter: ({ value }) => Number(value) || 0,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'total_asking_price',
      headerName: 'Total Sale',
      flex: 0.75,
      minWidth: 130,
      valueFormatter: ({ value }) => formatCurrency(value),
    },
    {
      field: 'total_sold_price',
      headerName: 'Total Sold',
      flex: 0.75,
      minWidth: 120,
      valueFormatter: ({ value }) => formatCurrency(value),
    },
    {
      headerName: '',
      flex: 0.55,
      minWidth: 78,
      cellRenderer: ({ data }) => (
        <div className="flex space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSale(data);
            }}
            className="text-sm font-normal text-red-400 hover:text-red-500 hover:underline"
          >
            Delete
          </button>
        </div>
      ),
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: false,
    resizable: false,
    singleClickEdit: true, // Enables single-click editing for editable cells
    suppressHeaderMenuButton: true,
  }), []);

  if (authLoading || !ready) {
    // Only block the page while AuthContext itself is still booting.
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        Loading...
      </div>
    );
  }

  const useAutoHeight = sales.length > 0 && sales.length <= 5;
  const tableHeight = sales.length === 0 ? 140 : sales.length <= 10 ? 260 : 320;
  const subscriptionStart = freshMembership?.start_date || membership?.start_date || null;
  const subscriptionEnd = freshMembership?.end_date || membership?.end_date || null;
  const maxSales = freshMembership?.max_sales ?? membership?.max_sales;
  const totalCredits = freshMembership?.total_images_avail ?? membership?.total_images_avail;
  const maxSalesValue = Number(maxSales);
  const maxSalesReached = Number.isFinite(maxSalesValue) && sales.length >= maxSalesValue;
  const fallbackDescriptionParts = [];
  if (subscriptionStart && subscriptionEnd) {
    const startTime = new Date(subscriptionStart).getTime();
    const endTime = new Date(subscriptionEnd).getTime();
    const days = Math.ceil((endTime - startTime) / (1000 * 60 * 60 * 24));
    if (Number.isFinite(days) && days > 0) {
      fallbackDescriptionParts.push(`${days} days`);
    }
  }
  if (typeof maxSales === 'number') {
    fallbackDescriptionParts.push(`${maxSales} active sales`);
  }
  if (typeof totalCredits === 'number') {
    fallbackDescriptionParts.push(`${totalCredits} upload credits`);
  }
  const fallbackDescription = fallbackDescriptionParts.join(', ');
  const membershipDescription =
    tierDetails?.description || freshMembership?.description || membership?.description || fallbackDescription;
  const descriptionParts = membershipDescription
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const planDetailText =
    descriptionParts.find((part) => /days|free/i.test(part) && !/\$/i.test(part)) || '';
  const filteredDescriptionParts = descriptionParts.filter(
    (part) => !/\$|\bper\s+mo\b|\bper\s+month\b/i.test(part)
  );
  const planDetailItems = filteredDescriptionParts.length ? filteredDescriptionParts : [];
  const planDetailsLeft = [
    planDetailItems.find((part) => /days/i.test(part)),
    planDetailItems.find((part) => /active sale/i.test(part)),
    planDetailItems.find((part) => /upload credits/i.test(part))
  ].filter(Boolean);
  const planDetailsRight = [
    planDetailItems.find((part) => /upload .*images/i.test(part)),
    planDetailItems.find((part) => /price tags/i.test(part))
  ].filter(Boolean);
  const membershipStatus = (freshMembership?.status || membership?.status || '').toLowerCase();
  const membershipTier = (freshMembership?.tier || membership?.tier || '').toLowerCase();
  const renewalLabel = membershipTier.includes('trial')
    ? 'Trial Ends On'
    : membershipStatus.includes('cancel')
    ? 'Ends On'
    : 'Renews On';
  const sanitizedDescription = filteredDescriptionParts.join(', ').replace(/\s+,/g, ',').trim();

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* <RouteBar /> — Rendered by PipelineLayout */}
      <div className="container mx-auto max-w-6xl px-6 pt-1 pb-6 space-y-5">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-6 py-4">
          <div className="text-[22px] font-semibold text-slate-900">Account Overview</div>
          <div className="mt-[-2px] text-base text-slate-600">
            Signed in as: {profile?.first_name || user?.email?.split('@')[0] || 'Client'}
          </div>
          <div className="mt-[-13px] text-[28px] md:text-[32px] font-bold text-slate-900 text-center">
            {clientInfo?.name || 'N/A'}
          </div>

          <div className="mt-0 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6 items-start">
            <div>
              <div className="w-full rounded-xl bg-slate-50 px-3 py-3">
                <div className="grid grid-cols-1 lg:grid-cols-[0.6fr_1fr] gap-3 items-stretch">
                  <div className="relative -top-1">
                    <div className="text-lg font-bold text-slate-900">Plan &amp; Billing</div>
                    <div className="text-sm font-semibold text-slate-700 mt-1">
                      {freshMembership?.tier || membership?.tier || 'N/A'}
                      {planDetailText ? ` — ${planDetailText}` : ''}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-slate-700 max-w-[300px]">
                      <div>
                        <div className="text-xs uppercase tracking-wide text-slate-500">Start Date</div>
                        <div className="font-semibold text-slate-900">
                          {subscriptionStart ? formatDate(subscriptionStart) : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-slate-500">{renewalLabel}</div>
                        <div className="font-semibold text-slate-900">
                          {subscriptionEnd ? formatDate(subscriptionEnd) : '—'}
                        </div>
                      </div>
                    </div>

                    {sanitizedDescription && (
                      <div className="mt-3 text-sm text-slate-700">
                        <div className="font-semibold text-slate-700">Plan Details</div>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-[max-content_max-content] gap-x-2 gap-y-2 justify-start">
                          <div className="space-y-2">
                            {planDetailsLeft.map((part) => (
                              <div key={part} className="whitespace-nowrap flex items-start gap-2">
                                <span className="text-slate-400">•</span>
                                <span>{part}</span>
                              </div>
                            ))}
                          </div>
                          <div className="space-y-2">
                            {planDetailsRight.map((part) => (
                              <div key={part} className="whitespace-nowrap text-slate-700 flex items-start gap-2">
                                <span className="text-slate-400">•</span>
                                <span>{part}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-center justify-end text-center lg:col-start-2 self-end w-full">
                    <div className="w-full flex flex-col items-center lg:translate-x-[-20px]">
                      <div className="text-xl md:text-2xl font-medium text-slate-900 leading-none mt-1">
                        {freshMembership?.total_images_avail || membership?.total_images_avail || 0}
                      </div>
                      <div className="text-sm font-semibold text-slate-800 mt-0.5">Upload Credits Remaining</div>
                      <div className="text-xs text-slate-500">Used only when you upload photos.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="min-w-[220px] flex flex-col gap-2 self-start mt-2">
              <button
                onClick={() => navigate('/estate-sales-pipeline/member-maint2')}
                className="bg-blue-600 text-white px-3 py-2 rounded-lg font-semibold text-base h-11 hover:bg-blue-700"
              >
                Manage Membership
              </button>
              <button
                onClick={() => navigate('/estate-sales-pipeline/update-profile')}
                className="bg-white border border-slate-300 text-slate-900 px-3 py-2 rounded-lg font-semibold text-base h-11 hover:bg-slate-50"
              >
                Update Profile
              </button>
              {profileCcCollectionEnabled && !isFreeMembership && (
                <button
                  type="button"
                  onClick={handleStripeDashboardOpen}
                  disabled={stripeConnectLoading}
                  className={[
                    'bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-lg font-semibold text-base min-h-11 hover:bg-emerald-100',
                    stripeConnectLoading ? 'opacity-60 cursor-not-allowed' : '',
                  ].join(' ')}
                >
                  {stripeConnectLoading ? 'Opening Stripe...' : 'Open Stripe Merchant Dashboard'}
                </button>
              )}
              {showStripeConnectCta && (
                <button
                  type="button"
                  onClick={handleStripeConnectStart}
                  disabled={stripeConnectLoading}
                  className={[
                    'bg-white border border-slate-300 text-slate-900 px-3 py-2 rounded-lg font-semibold text-base min-h-11 hover:bg-slate-50',
                    stripeConnectLoading ? 'opacity-60 cursor-not-allowed' : '',
                  ].join(' ')}
                >
                  {stripeConnectLoading ? 'Opening Stripe...' : 'Set Up Stripe Credit Cards'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowSuppliesModal(true)}
                className="bg-white border border-slate-300 text-slate-900 px-3 py-2 rounded-lg font-semibold text-base h-11 hover:bg-slate-50"
              >
                Labels &amp; Supplies
              </button>
              <button
                onClick={() => navigate('/estate-sales-pipeline/member-usage-log')}
                className="bg-white border border-slate-300 text-slate-900 px-3 py-2 rounded-lg font-semibold text-base h-11 hover:bg-slate-50"
              >
                Activity Log
              </button>
              <button
                onClick={() => navigate('/estate-sales-pipeline/sales-stats')}
                className="bg-white border border-slate-300 text-slate-900 px-3 py-2 rounded-lg font-semibold text-base h-11 hover:bg-slate-50"
              >
                Sale Performance
              </button>
            </div>
          </div>
        </div>

        {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>}
        {showUpdatedMessage && updatedType && (
          <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded text-center text-sm">
            <strong>Success!</strong>{' '}
            {updatedType === 'free'
              ? 'Free trial activated—25 credits for 30 days!'
              : updatedType.startsWith('paid')
              ? 'Paid membership activated!'
              : updatedType.startsWith('topup')
              ? 'Credits topped up—ready to upload!'
              : 'Membership updated!'}
          </div>
        )}
        {stripeConnectMessage && (
          <div
            className={[
              'mt-4 px-4 py-3 rounded text-center text-sm border',
              stripeConnectMessageType === 'success'
                ? 'bg-green-100 border-green-400 text-green-700'
                : stripeConnectMessageType === 'warning'
                  ? 'bg-amber-50 border-amber-300 text-amber-800'
                  : stripeConnectMessageType === 'error'
                    ? 'bg-red-100 border-red-400 text-red-700'
                    : 'bg-slate-50 border-slate-300 text-slate-700',
            ].join(' ')}
          >
            {renderStripeConnectMessage(stripeConnectMessage)}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-2">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-[22px] font-bold text-slate-900">Sales</h3>
            </div>
            <p className="text-sm mt-1 text-slate-500">
              Create and manage your estate sales here.
            </p>

            {saleCreatedMessage && (
              <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-center text-sm">
                Sale created. You can upload pictures now.
              </div>
            )}

            <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-4">
              <div className="text-base font-bold text-slate-700 mb-3">Create a Sale</div>

              {maxSalesReached && (
                <div className="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                  <div className="font-semibold text-slate-900">Sales limit reached</div>
                  <div>
                    Your plan allows {maxSalesValue} active sale{maxSalesValue === 1 ? '' : 's'}. Delete a sale to add another, or upgrade your account.
                  </div>
                </div>
              )}
              <div className="flex flex-col md:flex-row md:items-end gap-2 md:gap-5 lg:gap-4">
                <div className="w-full md:w-[370px] md:min-w-[280px] md:max-w-[420px] lg:flex-[1.9] lg:min-w-0 lg:max-w-none">
                  <label className="block text-base font-semibold text-slate-700 mb-1">Sale Name</label>
                  <input
                    type="text"
                    value={saleName}
                    onChange={(e) => setSaleName(e.target.value)}
                    placeholder="e.g., 1195 Virginia Ave"
                    className="w-full h-11 rounded-lg border border-slate-300 px-3 text-base"
                    disabled={loading || maxSalesReached}
                  />
                </div>

                <div className="w-full md:w-[75px] lg:flex-1 lg:min-w-0">
                  <label className="block text-base font-semibold text-slate-700 mb-1">Start Date</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={startDate}
                      onChange={(e) => setStartDate(formatDateInput(e.target.value))}
                      onBlur={() => setStartDate(normalizeDateInput(startDate))}
                      placeholder="MM/DD/YYYY"
                      className="w-full h-11 rounded-lg border border-slate-300 px-3 pr-10 text-base"
                      disabled={loading || maxSalesReached}
                      inputMode="numeric"
                    />
                    <button
                      type="button"
                      onClick={() => openDatePicker(startDatePickerRef)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center justify-center"
                      aria-label="Open calendar"
                      title="Open calendar"
                      disabled={loading || maxSalesReached}
                      tabIndex={startDate ? -1 : 0}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                    <input
                      ref={startDatePickerRef}
                      type="date"
                      className="absolute inset-0 h-full w-full opacity-0 pointer-events-none"
                      tabIndex={-1}
                      onChange={(e) => setStartDate(normalizeDateInput(e.target.value))}
                    />
                  </div>
                </div>

                <div className="w-full md:w-[75px] lg:flex-1 lg:min-w-0">
                  <label className="block text-base font-semibold text-slate-700 mb-1">End Date</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={endDate}
                      onChange={(e) => setEndDate(formatDateInput(e.target.value))}
                      onBlur={() => setEndDate(normalizeDateInput(endDate))}
                      placeholder="MM/DD/YYYY"
                      className="w-full h-11 rounded-lg border border-slate-300 px-3 pr-10 text-base"
                      disabled={loading || maxSalesReached}
                      inputMode="numeric"
                    />
                    <button
                      type="button"
                      onClick={() => openDatePicker(endDatePickerRef)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center justify-center"
                      aria-label="Open calendar"
                      title="Open calendar"
                      disabled={loading || maxSalesReached}
                      tabIndex={endDate ? -1 : 0}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                    <input
                      ref={endDatePickerRef}
                      type="date"
                      className="absolute inset-0 h-full w-full opacity-0 pointer-events-none"
                      tabIndex={-1}
                      onChange={(e) => setEndDate(normalizeDateInput(e.target.value))}
                    />
                  </div>
                </div>

                <div className="w-full md:w-[130px] lg:flex-1 lg:min-w-0 relative">
                  <label className="block text-base font-semibold text-slate-700 mb-1">ZIP Code</label>
                  <input
                    type="text"
                    value={saleZip}
                    onChange={(e) => {
                      setSaleZip(e.target.value);
                      setZipSuggestions([]);
                    }}
                    onFocus={() => saleZip.length >= 3 && fetchZipSuggestions(saleZip)}
                    placeholder="ZIP"
                    className="w-full h-11 rounded-lg border border-slate-300 px-3 text-base"
                    disabled={loading || maxSalesReached}
                  />

                  {zipSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-60 overflow-y-auto">
                      <div className="px-3 py-2 text-xs text-slate-500">Select your ZIP</div>
                      {zipSuggestions.map((s) => (
                        <div
                          key={s.postcode}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                          onClick={() => {
                            setSaleZip(s.postcode);
                            setZipSuggestions([]);
                          }}
                        >
                          {s.postcode} — {s.city}, {s.state}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="w-full md:w-[88px] lg:flex-[0.65] lg:min-w-0">
                  <label className="block text-base font-semibold text-slate-700 mb-1">Sales Tax %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={saleSalesTax}
                    onChange={(e) => setSaleSalesTax(e.target.value)}
                    className="w-full h-11 rounded-lg border border-slate-300 px-3 text-base"
                    disabled={loading || maxSalesReached}
                  />
                </div>

                <div className="w-full md:w-[160px] lg:flex-1 lg:min-w-0">
                  <button
                    onClick={handleCreateSale}
                    disabled={
                      loading ||
                      maxSalesReached ||
                      !saleName.trim() ||
                      !isValidDateInput(startDate) ||
                      !isValidUSZip(saleZip) ||
                      parsePercentValue(saleSalesTax) === null ||
                      (endDate && !isValidDateInput(endDate))
                    }
                    className={`w-full h-11 rounded-lg text-white text-base ${
                      loading ||
                      maxSalesReached ||
                      !saleName.trim() ||
                      !isValidDateInput(startDate) ||
                      !isValidUSZip(saleZip) ||
                      parsePercentValue(saleSalesTax) === null ||
                      (endDate && !isValidDateInput(endDate))
                        ? 'bg-gray-400 cursor-not-allowed font-semibold'
                        : 'bg-blue-600 hover:bg-blue-700 font-bold'
                    }`}
                  >
                    {loading ? 'Creating...' : 'Create Sale'}
                  </button>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-500">
                 {maxSalesReached
                  ? 'Sales limit reached for your plan. Delete a sale to create a new one.'
                  : 'Sale name and dates can be updated anytime. ZIP code is locked once the sale is created.'}
              </div>
            </div>

            <div className="mt-4 border-t border-slate-200" />

            <div className="mt-4">
              <div className="text-[18px] font-semibold text-slate-700">Your Sales</div>
            </div>
            <div
              className="mt-1.5 border border-slate-200 rounded-xl overflow-x-auto overflow-y-hidden bg-white pt-2 dashboard-sales-grid"
              style={useAutoHeight ? undefined : { height: tableHeight }}
            >
              <style>{`.dashboard-sales-grid .ag-header{background-color:#f1f5f9;} .dashboard-sales-grid .ag-header-cell-menu-button{display:none;}`}</style>
              {sales.length > 0 ? (
                <div className="ag-theme-quartz h-full w-full">
                  <AgGridReact
                    rowData={sales}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    suppressMultiSort={true}
                    pagination={sales.length > 10}
                    paginationPageSize={10}
                    domLayout={useAutoHeight ? 'autoHeight' : 'normal'}
                    enableCellTextSelection={true}
                    animateRows={false}
                    onCellValueChanged={onCellValueChanged}
                    stopEditingWhenCellsLoseFocus={true}
                    suppressRowHoverHighlight={true}
                    suppressMovableColumns={true}
                    suppressDragLeaveHidesColumns={true}
                    theme="legacy"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-base text-slate-500 min-h-[120px]">
                  No sales yet. Create your first sale above.
                </div>
              )}
            </div>
          </div>
        </div>

        {showDeleteModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={() => {
              setShowDeleteModal(false);
              setSaleToDelete(null);
              setDeleteMessage('');
            }}
          >
            <div
              className="w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-slate-200">
                <div className="text-lg font-semibold text-slate-900">Delete Sale</div>
              </div>
              <div className="px-5 py-4 text-slate-700 text-sm">{deleteMessage}</div>
              <div className="px-5 pb-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSaleToDelete(null);
                    setDeleteMessage('');
                  }}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteSale}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showDateErrorModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={() => {
              setShowDateErrorModal(false);
              setDateErrorMessage('');
            }}
          >
            <div
              className="w-full max-w-md bg-white rounded-xl shadow-xl border border-slate-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-slate-200">
                <div className="text-lg font-semibold text-slate-900">Date Error</div>
              </div>
              <div className="px-5 py-4 text-slate-700 text-sm">
                {dateErrorMessage}
              </div>
              <div className="px-5 pb-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDateErrorModal(false);
                    setDateErrorMessage('');
                  }}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {showSuppliesModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl border border-slate-200">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h4 className="text-lg font-extrabold text-slate-900">Labels &amp; Supplies</h4>
                <button
                  type="button"
                  onClick={() => setShowSuppliesModal(false)}
                  className="text-sm font-semibold text-slate-600 hover:text-slate-900"
                >
                  Close
                </button>
              </div>
              <div className="p-4">
          <div className="mb-3 flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  <span className="mt-0.5 text-slate-500" aria-hidden="true">Info</span>
                  <div>
                    <div>These links open to Amazon for your convenience.</div>
                    <div>Scucci does not sell labels and does not earn commissions from these links.</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="border border-slate-200 rounded-xl p-4 bg-white">
                    <div className="mb-3">
                      <p className="text-lg font-semibold text-gray-900">Bluetooth / On-site</p>
                      <p className="text-sm text-gray-700 leading-5">Best for printing on-site from your phone or tablet.</p>
                      <p className="text-sm text-gray-700 leading-5">Recommended for on-site pricing and mobile workflows.</p>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide text-gray-700">Printers</p>
                        <div className="space-y-2 divide-y divide-slate-100">
                          <div className="py-3">
                            <p className="text-base font-semibold text-gray-900">Phomemo M110 Thermal Label Maker</p>
                            <a
                              href="https://a.co/d/2ucAXvr"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-gray-900"
                            >
                              Buy on Amazon
                            </a>
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide text-gray-700">Labels</p>
                        <div className="space-y-2 divide-y divide-slate-100">
                          <div className="py-3">
                            <p className="text-base font-semibold text-gray-900">Phomemo Original Labels 30mm × 20mm (320 per roll)</p>
                            <p className="text-sm text-gray-700 leading-5">In Scucci: Print Tags → Phomemo 30×20</p>
                            <a
                              href="https://a.co/d/fOpoZ24"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-gray-900"
                            >
                              Buy on Amazon
                            </a>
                          </div>
                          <div className="py-3">
                            <p className="text-base font-semibold text-gray-900">Phomemo Original Labels 40mm × 30mm (230 per roll)</p>
                            <p className="text-sm text-gray-700 leading-5">In Scucci: Print Tags → Phomemo 40×30</p>
                            <a
                              href="https://a.co/d/5m9YRhy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-gray-900"
                            >
                              Buy on Amazon
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-xl p-4 bg-white">
                    <div className="mb-3">
                      <p className="text-lg font-semibold text-gray-900">Sheet Labels</p>
                      <p className="text-sm text-gray-700 leading-5">Standard Home or Office Printer (8.5 x 11in)</p>
                      <p className="text-sm text-gray-700 leading-5">Best for pre-printing tags from a desktop or laptop.</p>
                      <p className="text-xs text-slate-500 mt-1">Links open Amazon in a new tab.</p>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide text-gray-700">Labels</p>
                        <div className="space-y-2 divide-y divide-slate-100">
                          <div className="py-3">
                            <p className="text-base font-semibold text-gray-900">Avery 5160 — 1” × 2-5/8” (30 per sheet)</p>
                            <p className="text-sm text-gray-700 leading-5">In Scucci: Print Tags → Avery 5160</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <a
                                href="https://a.co/d/f9GwSJn"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-gray-900"
                              >
                                Laser Printer
                              </a>
                              <a
                                href="https://a.co/d/9s3Gtyd"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-gray-900"
                              >
                                Inkjet Printer
                              </a>
                            </div>
                          </div>
                          <div className="py-3">
                            <p className="text-base font-semibold text-gray-900">Avery 5167 — 0.5” × 1.75” (80 per sheet)</p>
                            <p className="text-sm text-gray-700 leading-5">In Scucci: Print Tags → Avery 5167</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <a
                                href="https://a.co/d/7kNvzYv"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-gray-900"
                              >
                                Laser Printer
                              </a>
                              <a
                                href="https://a.co/d/0So0gyG"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-gray-900"
                              >
                                Inkjet Printer
                              </a>
                            </div>
                          </div>
                          <div className="py-3">
                            <p className="text-base font-semibold text-gray-900">Avery 94102 — 0.75” × 0.75” (80 per sheet)</p>
                            <p className="text-sm text-gray-700 leading-5">In Scucci: Print Tags → Avery 94102</p>
                            <a
                              href="https://a.co/d/5jXBbPb"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-gray-900"
                            >
                              Laser/Inkjet
                            </a>
                          </div>
                          <div className="py-3">
                            <p className="text-base font-semibold text-gray-900">Premium Label Supply — 1” × 0.5” (80 per sheet)</p>
                            <p className="text-sm text-gray-700 leading-5">In Scucci: Print Tags → PLS275</p>
                            <a
                              href="https://a.co/d/1ZxbxK8"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-gray-900"
                            >
                              Laser/Inkjet
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 border-t border-slate-200 pt-3 text-sm text-gray-600">
                  Need help choosing labels or printers? Email <span className="font-semibold">support@scuccisystems.com</span> — we&apos;ll help you get set up.
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Scucci does not sell labels or printers and does not receive affiliate commissions.
                  These recommendations are provided solely to save you time.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserDashboard;
